import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormFeedback,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';

import { useTranslation } from 'react-i18next';
import { CourseCategory } from '../../../../../types/Tables';
import { CourseStateStatus } from '../../../../../types/Courses';
import { useAppSelector } from '../../../../../reduxToolkit/hooks';
import SimpleModal from '../../../../uiKits/modal/basicModal/SimpleModal';
import AddAdditionalMaterialForm from '../../../../courses/details/[id]/AddAdditionalMaterialForm';
import { useEffect, useMemo, useState } from 'react';
import { AdditionalMaterial } from '../../../../courses/details/[id]/CreateLesson';
import { baseLang } from '../../../../../lib/utils';
import { validateVideoFile } from '../../../../../lib/filesUtils';

type CreateProps = {
  courseId: number;
  name: string;
  courseStateStatus: CourseStateStatus;
  close: () => void;
  categories?: CourseCategory[];
  takenPositions: number[];
  createHandler: (
    courseId: number,
    lessonName: {
      en: { name: string; id: number };
      uk: { name: string; id: number };
      ru: { name: string; id: number };
    },
    lessonDescription: {
      en: { text: string; id: number };
      uk: { text: string; id: number };
      ru: { text: string; id: number };
    },
    file: File,
    token: string,
    category: { name: string; id: number },
    image: File | null,
    courseStateStatus: CourseStateStatus,
    lessonPosition: number,
    additionals?: AdditionalMaterial[],
    videoTitle?: {
      en: string;
      uk: string;
      ru: string;
    },
  ) => Promise<{ uploaded: boolean }>;
  setUploadSuccess: (success: boolean) => void;
  setShowConfirm: (show: boolean) => void;
};

const AddLessonForm: React.FC<CreateProps> = (props) => {
  const [lessonName, setLessonName] = useState({
    en: {
      name: '',
      id: 0,
    },
    uk: {
      name: '',
      id: 1,
    },
    ru: {
      name: '',
      id: 2,
    },
  });
  const [lessonDescription, setLessonDescription] = useState({
    en: {
      text: '',
      id: 3,
    },
    uk: {
      text: '',
      id: 4,
    },
    ru: {
      text: '',
      id: 5,
    },
  });
  const [videoTitle, setVideoTitle] = useState({
    en: '',
    uk: '',
    ru: '',
  });
  const [uploading, setUploading] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [lessonPosition, setLessonPosition] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [additionals, setAdditionals] = useState<AdditionalMaterial[]>([]);

  const [category, setCategory] = useState({
    name: '',
    id: 0,
  });
  const [courseState, setCourseState] = useState<CourseStateStatus>(props.courseStateStatus);
  const [addMaterialsOpen, setAddMaterialsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);

  const errors = useMemo(() => {
    const e: Record<string, string | null> = {};

    // Names
    e['name.en'] = lessonName.en.name.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonNameEn');
    e['name.uk'] = lessonName.uk.name.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonNameUk');
    e['name.ru'] = lessonName.ru.name.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonNameRu');

    // Descriptions
    e['desc.en'] = lessonDescription.en.text.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonDescrEn');
    e['desc.uk'] = lessonDescription.uk.text.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonDescrUk');
    e['desc.ru'] = lessonDescription.ru.text.trim()
      ? null
      : t('courses.lessons.lessonForm.enterLessonDescrRu');

    // Category
    e['category'] = category.id ? null : t('courses.lessons.lessonForm.enterCat');

    // Position
    const raw = lessonPosition as number | string | null | undefined;
    const n = typeof raw === 'string' ? Number(raw.trim()) : Number(raw);
    if (raw === '' || raw == null) e['position'] = t('courses.lessons.lessonForm.enterPos');
    else if (!Number.isInteger(n) || n <= 0)
      e['position'] = t('courses.lessons.lessonForm.enterPositivePos');
    else if (props.takenPositions?.includes(n))
      e['position'] = t('courses.lessons.lessonForm.enterNotTakenPos');
    else e['position'] = null;

    // File
    e['file'] = file ? (fileError ? fileError : null) : t('courses.lessons.lessonForm.enterVideo');

    e['video_title_en'] = videoTitle.en.trim()
      ? null
      : t('courses.lessons.lessonForm.enterVideoNameEn');
    e['video_title_uk'] = videoTitle.uk.trim()
      ? null
      : t('courses.lessons.lessonForm.enterVideoNameUk');
    e['video_title_ru'] = videoTitle.ru.trim()
      ? null
      : t('courses.lessons.lessonForm.enterVideoNameRu');
    return e;
  }, [
    lessonName,
    lessonDescription,
    category,
    lessonPosition,
    file,
    fileError,
    props.takenPositions,
    t,
    videoTitle,
  ]);
  // const formValid = useMemo(() => {
  //   const hasErrors = Object.values(errors).some(Boolean);
  //   return !hasErrors && !uploading;
  // }, [errors, uploading]);
  const token = useAppSelector((state: any) => state.auth?.token);

  const selectCategoryTranslation = (cat: CourseCategory) => {
    if (pageLang === 'en') {
      return cat.name;
    } else {
      if (!cat.translations || cat.translations.length === 0) {
        return cat.name;
      }
      const categoryTranslation = cat.translations.find(
        (el: { id: number; lang: string; name: string }) => el.lang === pageLang,
      );
      return categoryTranslation?.name ?? cat.name;
    }
  };
  const deleteAdditionalMaterial = (material: AdditionalMaterial) => {
    const kept = additionals.filter((el) => el.id !== material.id);
    setAdditionals(kept);
  };

  const handleSubmitCreateLesson = async () => {
    if (uploading) return; // ignore double-clicks
    setAttempted(true);

    // block if any errors
    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) return;
    // 1) pre-checks
    if (!file) {
      setFileError('No file selected.');
      return;
    }

    const err = await validateVideoFile(file, t);
    if (err) {
      setFileError(err);
      // Optional: clear invalid selection to re-enable button immediately
      setFile(null);
      // If you have a ref to the <input type="file">, also clear e.currentTarget.value
      return;
    }

    setUploading(true);
    try {
      const uploadRes = await props.createHandler(
        props.courseId,
        lessonName,
        lessonDescription,
        file,
        // fileLink,
        token,
        category,
        image,
        courseState,
        lessonPosition ?? 0,
        additionals,
        videoTitle,
      );

      if (uploadRes.uploaded) {
        props.setUploadSuccess(true);
        setUploading(false);
        setOpen(false);
        props.setShowConfirm(true);
        props.close();
      } else {
        props.setUploadSuccess(false);
        setUploading(false);
        setOpen(false);
        props.setShowConfirm(true);
      }
    } catch (error) {
      props.setUploadSuccess(false);
      setUploading(false);
      setOpen(false);
      props.setShowConfirm(true);
      console.error('Error uploading file:', error);
    } finally {
      setFile(null);
      setFileError(null);
      setUploading(false);
      // props.close();
    }
  };

  return (
    <>
      <div className="upload-file-modal-content">
        <h2 className="text-lg font-semibold mb-4">
          {t('courses.lessons.lessonForm.createNewLessson')}
        </h2>
        <Row>
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.courseParams')}
              </CardTitle>
              <Label className="d-flex flex-column">
                {t('courses.generalCourseTitle')}
                {/* <Input type="number" name="courseId" value={props.courseId} disabled hidden /> */}
                <Input type="hidden" name="courseId" value={String(props.courseId)} />
                <Input type="text" name="name" value={props.name} disabled />
              </Label>

              <Label className="w-full d-flex flex-column">
                {t('courses.lessons.lessonForm.adjustCourseState')}
                <Input
                  type="select"
                  name="courseStateStatus"
                  value={courseState}
                  onChange={(e) => {
                    const selected = e.target.value as CourseStateStatus;
                    setCourseState(selected);
                  }}
                >
                  {/* <option value="">{SelectCategorySelector}</option> */}
                  {Object.values(CourseStateStatus).map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </Input>
              </Label>
              <Label className="w-full d-flex flex-column">
                {t('courses.lessons.lessonForm.courseCategories')}

                <Input
                  type="select"
                  name="categories"
                  value={String(category.id)}
                  disabled={uploading}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value);
                    const selectedCategory = props.categories?.find(
                      (cat) => Number(cat.id) === selectedId,
                    );
                    if (selectedCategory) {
                      setCategory({
                        id: Number(selectedCategory.id),
                        name: selectedCategory.name ?? '',
                      });
                    } else {
                      setCategory({ id: 0, name: '' }); // reset if none
                    }
                  }}
                  invalid={attempted && !!errors['category']}
                >
                  <option value="">{t('courses.lessons.selectCatrgory')}</option>
                  {props.categories &&
                    props.categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {selectCategoryTranslation(cat)}
                      </option>
                    ))}
                </Input>
                <FormFeedback>{attempted && errors['category']}</FormFeedback>
              </Label>
              {/* </CardBody> */}
            </Card>
          </Col>

          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonNameFormSection')}
              </CardTitle>{' '}
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName')}

                <Input
                  type="text"
                  name="title"
                  value={lessonName.en.name}
                  disabled={uploading}
                  // onChange={(e) =>
                  //   setLessonName((prev) => {
                  //     return {
                  //       ...prev,
                  //       en: {
                  //         ...prev.en,
                  //         name: e.target.value,
                  //       },
                  //     };
                  //   })
                  // }
                  onChange={(e) =>
                    setLessonName((prev) => ({
                      ...prev,
                      en: { ...prev.en, name: e.target.value },
                    }))
                  }
                  invalid={attempted && !!errors['name.en']}
                />
                <FormFeedback>{attempted && errors['name.en']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName_uk')}

                <Input
                  type="text"
                  name="title"
                  value={lessonName.uk.name}
                  disabled={uploading}
                  onChange={(e) =>
                    setLessonName((prev) => {
                      return {
                        ...prev,
                        uk: {
                          ...prev.uk,
                          name: e.target.value,
                        },
                      };
                    })
                  }
                  invalid={attempted && !!errors['name.uk']}
                />
                <FormFeedback>{attempted && errors['name.uk']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName_ru')}

                <Input
                  type="text"
                  name="title"
                  value={lessonName.ru.name}
                  disabled={uploading}
                  onChange={(e) =>
                    setLessonName((prev) => {
                      return {
                        ...prev,
                        ru: {
                          ...prev.ru,
                          name: e.target.value,
                        },
                      };
                    })
                  }
                  invalid={attempted && !!errors['name.ru']}
                />
                <FormFeedback>{attempted && errors['name.ru']}</FormFeedback>
              </Label>{' '}
              {/* </CardBody> */}
            </Card>
          </Col>
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonDescriptionFormSection')}
              </CardTitle>{' '}
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription')}

                <Input
                  type="text"
                  name="title"
                  value={lessonDescription.en.text}
                  disabled={uploading}
                  onChange={(e) =>
                    setLessonDescription((prev) => {
                      return {
                        ...prev,
                        en: {
                          ...prev.en,
                          text: e.target.value,
                        },
                      };
                    })
                  }
                  invalid={attempted && !!errors['desc.en']}
                />
                <FormFeedback>{attempted && errors['desc.en']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription_uk')}

                <Input
                  type="text"
                  name="title"
                  value={lessonDescription.uk.text}
                  disabled={uploading}
                  onChange={(e) =>
                    setLessonDescription((prev) => {
                      return {
                        ...prev,
                        uk: {
                          ...prev.uk,
                          text: e.target.value,
                        },
                      };
                    })
                  }
                  invalid={attempted && !!errors['desc.uk']}
                />

                <FormFeedback>{attempted && errors['desc.uk']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription_ru')}

                <Input
                  type="text"
                  name="title"
                  value={lessonDescription.ru.text}
                  disabled={uploading}
                  onChange={(e) =>
                    setLessonDescription((prev) => {
                      return {
                        ...prev,
                        ru: {
                          ...prev.ru,
                          text: e.target.value,
                        },
                      };
                    })
                  }
                  invalid={attempted && !!errors['desc.ru']}
                />
                <FormFeedback>{attempted && errors['desc.ru']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.inputs.lessonPosition')}
                <Input
                  type="number"
                  name="lesson_pos"
                  value={String(lessonPosition) ?? ''}
                  disabled={uploading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const v = e.target.value;
                    if (v === '') return setLessonPosition(null); // allow clearing
                    const n = Number(v);
                    setLessonPosition(Number.isInteger(n) && n > 0 ? n : null);
                  }}
                  invalid={attempted && !!errors['position']}
                />
                <FormFeedback>{attempted && errors['position']}</FormFeedback>
              </Label>
            </Card>
          </Col>
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonAdditionalsFormSection')}
              </CardTitle>
              <Col sm="12">
                <Button onClick={() => setAddMaterialsOpen(true)} className="mb-3">
                  {t('courses.lessons.lessonForm.addMaterials')}
                </Button>
              </Col>
              {additionals && additionals.length > 0 && (
                <ListGroup>
                  {additionals.map((item, index) => {
                    const materialsTitleEn = item.title;
                    const translatedTitle = pageLang === 'uk' ? item.title_uk : item.title_ru;

                    return (
                      <ListGroupItem
                        key={index} // ideally use item.id if you have it
                        className="d-flex justify-content-between align-items-center mb-2"
                      >
                        <div>
                          <strong>{pageLang === 'en' ? materialsTitleEn : translatedTitle}</strong>{' '}
                          ({item.kind})
                        </div>
                        <div>
                          <a
                            href={
                              item.kind === 'file'
                                ? `${process.env.REACT_APP_BACKEND_STATIC}${item.url}`
                                : item.url
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('courses.lessons.additionalMaterials.view')}
                          </a>
                        </div>
                        <div>
                          <Button
                            color="danger"
                            outline
                            onClick={() => deleteAdditionalMaterial(item)}
                          >
                            <i className="icon-trash" />
                          </Button>
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              )}
              {/* </CardBody> */}
            </Card>
          </Col>
          <Col sm="6">
            <Card className="p-3">
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.attachVideoSetPosition')}
              </CardTitle>{' '}
              <Label className="d-flex flex-column pb-4">
                {t('courses.lessons.lessonForm.attachVideo')}
                <Input
                  type="file"
                  accept="video/*"
                  disabled={uploading}
                  // onChange={(e) => {
                  //   const files = e.target.files;
                  //   setFile(files && files.length > 0 ? files[0] : null);
                  // }}
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setFile(f);
                    if (!f) {
                      setFileError('No file selected.');
                      return;
                    }
                    const err = await validateVideoFile(f, t);
                    setFileError(err);
                  }}
                  invalid={attempted && !!errors['file']}
                />
                <FormFeedback>{attempted && errors['file']}</FormFeedback>
                {file && !fileError && (
                  <small className="text-muted mt-1">
                    {file.name} ({Math.round(file.size / (1024 * 1024))} MB)
                  </small>
                )}
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.addLessonCover')}
                <Input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => {
                    const files = e.target.files;
                    setImage(files && files.length > 0 ? files[0] : null);
                  }}
                  className="mb-4"
                />
              </Label>
              {/* </CardBody> */}
            </Card>
          </Col>
          <Col sm="6">
            <Card className="p-3">
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.provideVideoName')}
              </CardTitle>{' '}
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.videoNameEn')}
                <Input
                  type="text"
                  name="video_title_en"
                  value={videoTitle.en}
                  disabled={uploading}
                  onChange={(e) =>
                    setVideoTitle((prev) => ({
                      ...prev,
                      en: e.target.value,
                    }))
                  }
                  invalid={attempted && !!errors['video_title_en']}
                />
                <FormFeedback>{attempted && errors['video_title_en']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.videoNameUk')}
                <Input
                  type="text"
                  name="video_title_uk"
                  value={videoTitle.uk}
                  disabled={uploading}
                  onChange={(e) =>
                    setVideoTitle((prev) => ({
                      ...prev,
                      uk: e.target.value,
                    }))
                  }
                  invalid={attempted && !!errors['video_title_uk']}
                />
                <FormFeedback>{attempted && errors['video_title_uk']}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.videoNameRu')}
                <Input
                  type="text"
                  name="video_title_ru"
                  value={videoTitle.ru}
                  disabled={uploading}
                  onChange={(e) =>
                    setVideoTitle((prev) => ({
                      ...prev,
                      ru: e.target.value,
                    }))
                  }
                  invalid={attempted && !!errors['video_title_ru']}
                />
                <FormFeedback>{attempted && errors['video_title_ru']}</FormFeedback>
              </Label>
            </Card>
          </Col>
        </Row>
        <Button
          variant="primary"
          // disabled={!formValid}
          className={`mt-4 ${
            uploading ? 'cursor-not-allowed' : ''
          } bg-green-200 hover:bg-green-300`}
          onClick={async () => {
            handleSubmitCreateLesson();
          }}
        >
          {uploading ? t('global.uploading') : t('global.upload')}
        </Button>
      </div>
      <SimpleModal
        isOpen={addMaterialsOpen}
        close={() => setAddMaterialsOpen(false)}
        size={'lg'}
        centered
      >
        <AddAdditionalMaterialForm
          addMaterial={(material) => {
            setAdditionals((prev) => [...prev, material]);
            setAddMaterialsOpen(false);
          }}
          close={() => setAddMaterialsOpen(false)}
        />
      </SimpleModal>
    </>
  );
};

export default AddLessonForm;
